import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { useSnackbar } from '../../../../context/snackbar-context';
import request from "../../../../util/SuperagentUtils";
import { adminBookingsReportEndpoints } from "../../../../requests/api-endpoints";
import { ReportDialogProps } from "../../../../interfaces";
import { AdminBookingReportVO } from "../../../../types/model/valueObject/booking/AdminBookingReportVO";
import { NextInvoicePreviewVO } from "../../../../types/model/valueObject/booking/NextInvoicePreviewVO";
import { getErrorMessage } from "../../../../util/NetworkErrorUtil";
import { formatCurrencyValue } from "../../../../util/PaymentUtils";

const cannedMessages = {
  genericError: 'Unable to create the next invoice. Please contact support.',
  previewError: 'Unable to preview the next invoice for this booking.',
  success: (orderNumber: string) => `Successfully created the next invoice for booking ${orderNumber}.`,
};

const CreateNextInvoiceDialog = ({
  item,
  open,
  onClose,
}: ReportDialogProps<AdminBookingReportVO>) => {
  const { snackbarShowMessage } = useSnackbar();

  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState<string | undefined>(undefined);
  const [preview, setPreview] = useState<NextInvoicePreviewVO | undefined>(undefined);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && item) {
      setPreview(undefined);
      setPreviewError(undefined);
      setLoadingPreview(true);
      request.get(adminBookingsReportEndpoints.actions.previewNextInvoice(item.id))
        .then((res) => setPreview(res.body))
        .catch((err) => setPreviewError(getErrorMessage(err, cannedMessages.previewError)))
        .finally(() => setLoadingPreview(false));
    }
  }, [open, item]);

  const handleConfirm = () => {
    if (!item) {
      snackbarShowMessage(cannedMessages.genericError, 'error');
      onClose({ item, action: 'ERROR' });
      return;
    }
    setSubmitting(true);
    request.post(adminBookingsReportEndpoints.actions.createNextInvoice(item.id))
      .then(() => {
        snackbarShowMessage(cannedMessages.success(item.orderNumber), 'success');
        onClose({ item, action: 'SUCCESS' });
      })
      .catch((err) => snackbarShowMessage(getErrorMessage(err, cannedMessages.genericError), 'error'))
      .finally(() => setSubmitting(false));
  };

  return (
    <Dialog open={open && Boolean(item)} onClose={() => onClose({ item })}>
      <DialogTitle>Create Next Invoice for {item?.orderNumber}</DialogTitle>
      <DialogContent>
        {loadingPreview && (
          <Box display="flex" justifyContent="center" p={2}>
            <CircularProgress size={28} />
          </Box>
        )}
        {!loadingPreview && previewError && (
          <Alert severity="error">{previewError}</Alert>
        )}
        {!loadingPreview && !previewError && preview && (
          <Stack spacing={1} pt={1}>
            <Typography variant="body2">
              <strong>Service Dates:</strong> {preview.serviceStartDate} - {preview.serviceEndDate}
            </Typography>
            <Typography variant="body2">
              <strong>Payment Number:</strong> {preview.paymentNumber}
            </Typography>
            <Typography variant="body2">
              <strong>Gross Amount:</strong> {formatCurrencyValue(preview.transactionGrossAmount, true)}
            </Typography>
            <Typography variant="body2">
              <strong>Tax Amount:</strong> {formatCurrencyValue(preview.transactionTaxAmount, true)}
            </Typography>
            <Typography variant="body2">
              <strong>Buyer Total:</strong> {formatCurrencyValue(preview.buyerTransactionAmount, true)}
            </Typography>
            {preview.previousInvoiceUnpaid && (
              <Alert severity="warning">
                The previous invoice for this booking has not been paid yet. You can still create
                the next invoice.
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={() => onClose({ item })}>
          Cancel
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={handleConfirm}
          disabled={loadingPreview || Boolean(previewError) || !preview || submitting}
        >
          Create Invoice
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export { CreateNextInvoiceDialog };
